import { getAdjustedPricePerPerson } from '@/components/event-simulator/utils/pricingRangeUtils';
import {
  getSpecialPricingExtra,
  parseSpecialPricingTiers,
  type SpecialPricingTier,
} from '@/components/event-simulator/utils/pricingAdjustmentUtils';
import { getSpecialGuestsCount } from '@/utils/specialGuests';
import { resolveItemEventType, type CartItem } from '@/utils/cartStorage';

/**
 * Pessoas que definem a FAIXA de preço: convidados + crianças/staff das categorias visíveis (as que de fato entram na conta).
 * 20 convidados + 15 staff = 35 pessoas -> faixa de 30 a 39. Sem convidados, não há faixa a definir.
 */
export const getRangeGuestCount = (
  guestCount: number,
  tiers: SpecialPricingTier[] | null | undefined,
  quantities: Record<string, number> | null | undefined,
): number => {
  if (!(guestCount > 0) || !Array.isArray(tiers) || !quantities) return guestCount;
  const extra = tiers.reduce((sum, t) => {
    const n = Number(quantities[t.id]);
    return sum + (Number.isFinite(n) && n > 0 ? Math.round(n) : 0);
  }, 0);
  return guestCount + extra;
};

/**
 * Valor adicional (R$) das categorias especiais (crianças, staff...) de um cardápio por convidado: cada pessoa paga o
 * percentual da categoria sobre o preço por pessoa da faixa. É a mesma conta do cartão de reserva, para o cartão da /loja
 * e o carrinho mostrarem o mesmo total que o cliente vê no cardápio.
 */
export const getSpecialExtraForProduct = (
  product: unknown,
  guestCount: number,
  tiers: SpecialPricingTier[] | null | undefined,
  quantities: Record<string, number> | null | undefined,
  platformTax?: number,
  date?: Date | string | null,
  eventType?: string | null,
): number => {
  if (!Array.isArray(tiers) || tiers.length === 0 || guestCount < 1) return 0;
  if (getSpecialGuestsCount(quantities) <= 0) return 0;
  const pricePerGuest = getAdjustedPricePerPerson(
    product,
    guestCount,
    platformTax,
    date ?? null,
    eventType ?? null,
    getRangeGuestCount(guestCount, tiers, quantities),
  );
  return getSpecialPricingExtra(pricePerGuest, tiers, quantities);
};

/**
 * Preço por convidado de TABELA do item do carrinho (faixa pelos convidados + crianças/staff, tipo de evento, temporada,
 * taxa da plataforma). É a base do percentual das crianças/staff, a mesma do cartão de reserva. Cardápio fixo ou de
 * seleção não cobra crianças/staff (o cartão de reserva também não): base 0.
 */
export const getCartItemBasePricePerGuest = (item: CartItem, platformTax?: number): number => {
  if (item.isFixedMenu || item.isSelectableMenu) return 0;
  const guests = parseInt(String(item.guests ?? ''), 10) || 0;
  if (guests < 1) return 0;
  const tiers = parseSpecialPricingTiers(item.specialPricingTiers);
  return getAdjustedPricePerPerson(
    {
      id: item.productId,
      price: item.price,
      special_pricing: item.specialPricing,
      special_price: item.specialPrice,
      price_ranges: (item as any).priceRanges,
      yearly_adjustments: item.yearlyAdjustments,
      seasonal_groups: item.seasonalGroups,
      event_type_adjustments: item.eventTypeAdjustments,
    },
    guests,
    platformTax,
    item.date || null,
    resolveItemEventType(item),
    getRangeGuestCount(guests, tiers, item.specialGuests as Record<string, number> | undefined),
  );
};

/** Categorias do item do carrinho (congeladas na hora em que entrou), ou as da loja quando o item não as trouxe. */
export const tiersForCartItem = (item: { specialPricingTiers?: unknown } | null | undefined, fallback: SpecialPricingTier[]): SpecialPricingTier[] => {
  const own = parseSpecialPricingTiers(item?.specialPricingTiers);
  return own.length > 0 ? own : fallback;
};

/** Taxa da plataforma da sessão (mesma que a loja usa para calcular os preços). */
export const readSessionCheckoutTax = (): number | undefined => {
  try {
    const n = Number(sessionStorage.getItem('ameup_checkout_tax'));
    return Number.isFinite(n) && n > 0 ? n : undefined;
  } catch {
    return undefined;
  }
};

/** Quantidades de crianças/staff que o cliente informou no simulador da loja (sessão do navegador). */
export const readSessionSpecialGuests = (): Record<string, number> => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem('ameup_special_guests') || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};
